import React from "react";
import { dash, formatDDMMYYYY } from "@/lib/documentUtils";

/**
 * Address Memo — latest layout:
 *   • Case Number on the RIGHT side of the case header
 *   • Two-column table: "Name and Address" (combined) | "Signature" (blank)
 *   • Verification heading + verification clause kept as-is
 *   • Place + Date lines (left-aligned) appear AFTER the verification clause
 *   • Final signature area is just a single blank horizontal line
 */
export const AddressMemo = React.forwardRef(function AddressMemo({ data }, ref) {
  const {
    caseNo, courtName, courtPlace,
    party1Name, party2Name, party1Role, party2Role,
    partyAddresses = [], date,
    advocateAppearingFor, advocateAppearingNumber,
  } = data;

  const appearLabel = `${dash(advocateAppearingFor, "Appellant")}${
    advocateAppearingNumber && String(advocateAppearingNumber).trim().length
      ? ` No. ${String(advocateAppearingNumber).trim()}`
      : ""
  }`;
  const appearLower = dash(advocateAppearingFor, "Appellant").toLowerCase();
  const filedDate = formatDDMMYYYY(date);
  const placeText = dash(courtPlace, "____________");

  const validRows = partyAddresses.filter(
    (p) => (p.name && p.name.trim()) || (p.address && p.address.trim()),
  );

  return (
    <div ref={ref} className="a4-sheet memo-sheet" data-testid="address-memo-doc">
      <h1 className="doc-title memo-title">ADDRESS MEMO</h1>

      <div className="memo-case-row">
        <div className="memo-case-spacer" />
        <div className="memo-case-no-right" data-testid="memo-case-no">
          {dash(caseNo, "Reg. _____________ No. ____ / _______")}
        </div>
      </div>

      <h2 className="doc-court-heading">IN THE COURT OF {dash(courtName, "_____________________")}</h2>
      <h3 className="doc-court-at">AT- {dash(courtPlace, "_____________________")}</h3>

      <div className="party-block">
        <div className="pb-row">
          <div className="pb-name">{dash(party1Name, "____________________")}</div>
          <div className="pb-brace">{"}"}</div>
          <div className="pb-role">
            Appellant / Applicant
            <br />
            Complainant / Petitioner
          </div>
        </div>
        <div className="pb-vs">V/s</div>
        <div className="pb-row">
          <div className="pb-name">{dash(party2Name, "____________________")}</div>
          <div className="pb-brace">{"}"}</div>
          <div className="pb-role">
            Respondent / Opponent
            <br />
            Accused / Defendant
          </div>
        </div>
      </div>

      <p className="memo-preamble doc-intro-line">Herein Address Memo of {appearLabel}.</p>

      <table className="memo-table" data-testid="address-memo-table">
        <thead>
          <tr>
            <th>Name and Address</th>
            <th style={{ width: "32%" }}>Signature</th>
          </tr>
        </thead>
        <tbody>
          {validRows.length === 0 ? (
            <tr>
              <td>
                <div style={{ fontWeight: 600 }}>
                  {dash(party1Name, "____________________")}
                </div>
                <div>R/o. ____________________________________________</div>
              </td>
              <td className="sig-cell">&nbsp;</td>
            </tr>
          ) : (
            validRows.map((p, idx) => (
              <tr key={idx}>
                <td>
                  <div style={{ fontWeight: 600 }}>
                    {dash(p.name, "____________________")}
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {dash(p.address, "____________________")}
                  </div>
                </td>
                <td className="sig-cell">&nbsp;</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h3 className="memo-verification-title">Verification</h3>
      <p className="memo-verification-body">
        I / We above named {appearLower} verify that, above mentioned address is true and
        correct as per today&apos;s our address hence verify.
      </p>

      {/* Place + Date — left aligned, after the verification clause */}
      <div className="memo-place-date" data-testid="memo-place-date">
        <div>
          <span className="memo-pd-label">Place:</span>&nbsp;{placeText}
        </div>
        <div>
          <span className="memo-pd-label">Date:</span>&nbsp;{filedDate}
        </div>
      </div>

      {/* Signature area: verified names shown on the RIGHT side, only after
          successful verification (at least one name/address row has been
          filled). No horizontal line — names only. Fallback: single blank
          signature line when no rows are filled. */}
      {validRows.length > 0 ? (
        <div className="memo-signatures" data-testid="memo-verified-names">
          {validRows.map((p, idx) => (
            <div key={idx} className="memo-sig-block">
              <div className="memo-sig-name">{p.name}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="memo-blank-sign" data-testid="memo-blank-signature">
          <div className="memo-blank-sign-line" />
        </div>
      )}
    </div>
  );
});

export default AddressMemo;
